import React, { useState, useEffect } from 'react';
import { FiX, FiCheck, FiAlertTriangle, FiDollarSign, FiCalendar, FiUser, FiShield } from 'react-icons/fi';
import { propertyAPI, tenantAPI, leaseAPI, litigationAPI } from '../../services/api';
import { toast } from 'react-toastify';

const DISPUTE_TYPES = [
  { value: 'unpaid_rent', label: 'Loyers Impayés & Charges Arriérées' },
  { value: 'property_damage', label: 'Dégradations du Bien / Défaut d\'entretien' },
  { value: 'illegal_sublease', label: 'Sous-location Illégale non autorisée' },
  { value: 'lease_violation', label: 'Violation des Clauses du Bail' },
  { value: 'refusal_to_vacate', label: 'Refus de Libérer les Lieux (Déguerpissement)' },
  { value: 'deposit_dispute', label: 'Litige Dépôt de Garantie / Caution' },
  { value: 'other', label: 'Autre Différend Locatif' },
];

const PHASES = [
  { value: 'amicable', label: '1. Phase Amiable (Relance / Négociation)' },
  { value: 'formal_notice', label: '2. Mise en Demeure 30j (Art. 35 Loi 2016-037)' },
  { value: 'bailiff_commandment', label: '3. Commandement de Payer (Huissier de Justice)' },
  { value: 'court_referral', label: '4. Saisine du Tribunal (Référé Expulsion)' },
  { value: 'court_hearing', label: '5. Audience en Cours / Délibéré' },
  { value: 'judgment_rendered', label: '6. Jugement / Ordonnance d\'Expulsion Rendue' },
  { value: 'enforcement_eviction', label: '7. Exécution Forcée & Expulsion (Force Publique)' },
  { value: 'settled_closed', label: '8. Accord Respecté / Dossier Clos' },
  { value: 'abandoned', label: '9. Procédure Abandonnée' },
];

const LitigationFormModal = ({ isOpen, onClose, onSuccess, litigationToEdit }) => {
  const isEditing = Boolean(litigationToEdit);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    tenant_id: '',
    property_id: '',
    lease_contract_id: '',
    dispute_type: 'unpaid_rent',
    phase: 'formal_notice',
    status: 'open',
    unpaid_amount: '',
    penalty_amount: '',
    bailiff_fees: '',
    lawyer_fees: '',
    court_fees: '',
    formal_notice_sent_date: new Date().toISOString().split('T')[0],
    bailiff_commandment_date: '',
    court_hearing_date: '',
    judgment_date: '',
    judgment_reference: '',
    bailiff_name: '',
    bailiff_phone: '',
    bailiff_email: '',
    lawyer_name: '',
    lawyer_phone: '',
    lawyer_email: '',
    court_name: 'Tribunal d\'Instance de Bamako',
    description: '',
  });

  useEffect(() => {
    if (isOpen) {
      loadDependencies();
      if (litigationToEdit) {
        setFormData({
          tenant_id: litigationToEdit.tenant_id || '',
          property_id: litigationToEdit.property_id || '',
          lease_contract_id: litigationToEdit.lease_contract_id || '',
          dispute_type: litigationToEdit.dispute_type || 'unpaid_rent',
          phase: litigationToEdit.phase || 'formal_notice',
          status: litigationToEdit.status || 'open',
          unpaid_amount: litigationToEdit.unpaid_amount || '',
          penalty_amount: litigationToEdit.penalty_amount || '',
          bailiff_fees: litigationToEdit.bailiff_fees || '',
          lawyer_fees: litigationToEdit.lawyer_fees || '',
          court_fees: litigationToEdit.court_fees || '',
          formal_notice_sent_date: litigationToEdit.formal_notice_sent_date ? litigationToEdit.formal_notice_sent_date.split('T')[0] : '',
          bailiff_commandment_date: litigationToEdit.bailiff_commandment_date ? litigationToEdit.bailiff_commandment_date.split('T')[0] : '',
          court_hearing_date: litigationToEdit.court_hearing_date ? litigationToEdit.court_hearing_date.split('T')[0] : '',
          judgment_date: litigationToEdit.judgment_date ? litigationToEdit.judgment_date.split('T')[0] : '',
          judgment_reference: litigationToEdit.judgment_reference || '',
          bailiff_name: litigationToEdit.bailiff_name || '',
          bailiff_phone: litigationToEdit.bailiff_phone || '',
          bailiff_email: litigationToEdit.bailiff_email || '',
          lawyer_name: litigationToEdit.lawyer_name || '',
          lawyer_phone: litigationToEdit.lawyer_phone || '',
          lawyer_email: litigationToEdit.lawyer_email || '',
          court_name: litigationToEdit.court_name || 'Tribunal d\'Instance de Bamako',
          description: litigationToEdit.description || '',
        });
      } else {
        setFormData({
          tenant_id: '',
          property_id: '',
          lease_contract_id: '',
          dispute_type: 'unpaid_rent',
          phase: 'formal_notice',
          status: 'open',
          unpaid_amount: '',
          penalty_amount: '',
          bailiff_fees: '',
          lawyer_fees: '',
          court_fees: '',
          formal_notice_sent_date: new Date().toISOString().split('T')[0],
          bailiff_commandment_date: '',
          court_hearing_date: '',
          judgment_date: '',
          judgment_reference: '',
          bailiff_name: '',
          bailiff_phone: '',
          bailiff_email: '',
          lawyer_name: '',
          lawyer_phone: '',
          lawyer_email: '',
          court_name: 'Tribunal d\'Instance de Bamako',
          description: '',
        });
      }
    }
  }, [isOpen, litigationToEdit]);

  const loadDependencies = async () => {
    try {
      const [propRes, tenantRes, leaseRes] = await Promise.all([
        propertyAPI.list({ limit: 100 }),
        tenantAPI.list({ limit: 100 }),
        leaseAPI.list({ limit: 100 }),
      ]);
      setProperties(propRes.data.data || propRes.data || []);
      setTenants(tenantRes.data.data || tenantRes.data || []);
      setLeases(leaseRes.data.data || leaseRes.data || []);
    } catch (e) {
      toast.error('Erreur chargement des données');
    }
  };

  const handleTenantChange = (tenantId) => {
    const matchingLease = leases.find(l => String(l.tenant_id) === String(tenantId));
    setFormData(prev => ({
      ...prev,
      tenant_id: tenantId,
      lease_contract_id: matchingLease?.id || prev.lease_contract_id,
      property_id: matchingLease?.property_id || prev.property_id,
      unpaid_amount: prev.unpaid_amount || (matchingLease?.monthly_rent ? String(matchingLease.monthly_rent * 2) : ''),
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Calcul dynamique du total réclamé
  const totalClaimed = (parseFloat(formData.unpaid_amount || 0) +
                       parseFloat(formData.penalty_amount || 0) +
                       parseFloat(formData.bailiff_fees || 0) +
                       parseFloat(formData.lawyer_fees || 0) +
                       parseFloat(formData.court_fees || 0));

  // Date limite légale 30 jours (Art. 35 Loi 2016-037)
  let calculatedDeadline = null;
  if (formData.formal_notice_sent_date) {
    const d = new Date(formData.formal_notice_sent_date);
    d.setDate(d.getDate() + 30);
    calculatedDeadline = d.toLocaleDateString('fr-FR');
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tenant_id) return toast.warn('Veuillez sélectionner le locataire concerné.');
    if (!formData.property_id) return toast.warn('Veuillez sélectionner le bien immobilier.');

    setSaving(true);
    try {
      if (isEditing) {
        await litigationAPI.update(litigationToEdit.id, formData);
        toast.success('Dossier de contentieux mis à jour avec succès !');
      } else {
        await litigationAPI.create(formData);
        toast.success('Dossier de contentieux ouvert avec succès !');
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'enregistrement du dossier');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={overlayStyle}>
      <div className="modal-content card" style={modalContentStyle}>
        {/* ── Header Sticky ── */}
        <div style={modalHeaderStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a' }}>
              <FiAlertTriangle color="#dc2626" size={20} />
              {isEditing ? `Modifier le Contentieux #${litigationToEdit.case_number}` : 'Nouveau Dossier de Contentieux & Litige Locatif'}
            </h2>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--text-secondary, #64748b)' }}>
              Procédure conforme à la Loi N° 2016-037 portant organisation du bail d'habitation au Mali.
            </p>
          </div>
          <button onClick={onClose} style={closeBtnStyle} title="Fermer"><FiX size={20} /></button>
        </div>

        {/* ── Corps du Formulaire avec Défilement Fluide ── */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
            
            {/* ── SECTION 1: Locataire, Bien & Contrat ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionTitleStyle}>
                <FiUser size={15} color="#2563eb" /> 1. Locataire, Bien & Contrat en Cause
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Locataire en Défaut *</label>
                  <select 
                    className="form-control" 
                    value={formData.tenant_id} 
                    onChange={e => handleTenantChange(e.target.value)}
                    required
                  >
                    <option value="">— Sélectionner le locataire —</option>
                    {tenants.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.first_name} {t.last_name} ({t.phone || t.email || 'Locataire'})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Bien Immobilier Occupé *</label>
                  <select 
                    className="form-control" 
                    value={formData.property_id} 
                    onChange={e => handleChange('property_id', e.target.value)}
                    required
                  >
                    <option value="">— Choisir le bien —</option>
                    {properties.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.title} ({p.city || 'Bamako'})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Contrat de Bail Lié</label>
                  <select 
                    className="form-control" 
                    value={formData.lease_contract_id} 
                    onChange={e => handleChange('lease_contract_id', e.target.value)}
                  >
                    <option value="">— Aucun contrat spécifique —</option>
                    {leases.map(l => (
                      <option key={l.id} value={l.id}>
                        Bail {l.contract_number} ({parseFloat(l.monthly_rent || 0).toLocaleString('fr-FR')} XOF/mois)
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* ── SECTION 2: Motif & Phase Procédurale ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionTitleStyle}>
                <FiShield size={15} color="#7c3aed" /> 2. Motif du Litige & Phase Procédurale (Droit Malien)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Motif Principal du Litige *</label>
                  <select 
                    className="form-control" 
                    value={formData.dispute_type} 
                    onChange={e => handleChange('dispute_type', e.target.value)}
                    required
                  >
                    {DISPUTE_TYPES.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Phase Procédurale Actuelle *</label>
                  <select 
                    className="form-control" 
                    value={formData.phase} 
                    onChange={e => handleChange('phase', e.target.value)}
                    required
                  >
                    {PHASES.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Statut du Dossier *</label>
                  <select 
                    className="form-control" 
                    value={formData.status} 
                    onChange={e => handleChange('status', e.target.value)}
                    required
                  >
                    <option value="open">Ouvert / En Traitement</option>
                    <option value="in_progress">Procédure Judiciaire Active</option>
                    <option value="suspended">Suspendu (Négociation en cours)</option>
                    <option value="resolved">Résolu / Soldé</option>
                    <option value="closed">Clos (Expulsion réalisée / Archivé)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* ── SECTION 3: Décompte Financier ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionTitleStyle}>
                <FiDollarSign size={15} color="#dc2626" /> 3. Décompte des Sommes Réclamées (XOF)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Principal Loyers Impayés *</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    placeholder="0"
                    value={formData.unpaid_amount} 
                    onChange={e => handleChange('unpaid_amount', e.target.value)} 
                    required
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Pénalités & Intérêts</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    placeholder="0"
                    value={formData.penalty_amount} 
                    onChange={e => handleChange('penalty_amount', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Frais d'Huissier (Actes)</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    placeholder="0"
                    value={formData.bailiff_fees} 
                    onChange={e => handleChange('bailiff_fees', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Honoraires Avocat</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    placeholder="0"
                    value={formData.lawyer_fees} 
                    onChange={e => handleChange('lawyer_fees', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Dépens & Greffe</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    placeholder="0"
                    value={formData.court_fees} 
                    onChange={e => handleChange('court_fees', e.target.value)} 
                  />
                </div>
              </div>

              {/* Total Réclamé Banner */}
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#fee2e2', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#991b1b' }}>TOTAL GLOBAL DE LA CRÉANCE RÉCLAMÉE :</span>
                <strong style={{ fontSize: 16, color: '#dc2626' }}>{totalClaimed.toLocaleString('fr-FR')} XOF</strong>
              </div>
            </div>

            {/* ── SECTION 4: Dates Procédurales & Délais ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionTitleStyle}>
                <FiCalendar size={15} color="#d97706" /> 4. Dates Procédurales & Délais Impératifs (Loi 2016-037 Art. 35)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Date Envoi Mise en Demeure</label>
                  <input 
                    type="date" 
                    className="form-control" 
                    value={formData.formal_notice_sent_date} 
                    onChange={e => handleChange('formal_notice_sent_date', e.target.value)} 
                  />
                  {calculatedDeadline && (
                    <div style={{ fontSize: 11, color: '#991b1b', fontWeight: 600, marginTop: 3 }}>
                      ⏳ Expiration 30j : <strong>{calculatedDeadline}</strong>
                    </div>
                  )}
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Date Commandement Huissier</label>
                  <input 
                    type="date" 
                    className="form-control" 
                    value={formData.bailiff_commandment_date} 
                    onChange={e => handleChange('bailiff_commandment_date', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Date Audience Tribunal</label>
                  <input 
                    type="date" 
                    className="form-control" 
                    value={formData.court_hearing_date} 
                    onChange={e => handleChange('court_hearing_date', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Date & Réf. Jugement / Ordonnance</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={formData.judgment_date} 
                      onChange={e => handleChange('judgment_date', e.target.value)} 
                    />
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="Réf N°..."
                      value={formData.judgment_reference} 
                      onChange={e => handleChange('judgment_reference', e.target.value)} 
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── SECTION 5: Auxiliaires de Justice & Juridiction ── */}
            <div style={sectionBoxStyle}>
              <div style={sectionTitleStyle}>
                <FiShield size={15} color="#059669" /> 5. Auxiliaires de Justice & Juridiction Compétente
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Huissier de Justice</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Ex: Me Coulibaly, Huissier"
                    value={formData.bailiff_name} 
                    onChange={e => handleChange('bailiff_name', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Contact Huissier (Tél)</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="+223 ..."
                    value={formData.bailiff_phone} 
                    onChange={e => handleChange('bailiff_phone', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Avocat Mandaté</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Ex: Cabinet Me Diallo"
                    value={formData.lawyer_name} 
                    onChange={e => handleChange('lawyer_name', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={labelStyle}>Tribunal Compétent</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={formData.court_name} 
                    onChange={e => handleChange('court_name', e.target.value)} 
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1', margin: 0 }}>
                  <label style={labelStyle}>Exposé des Faits & Notes de Procédure</label>
                  <textarea 
                    rows={2}
                    className="form-control" 
                    placeholder="Circonstances du défaut de paiement, tentatives de conciliation amiable, etc."
                    value={formData.description} 
                    onChange={e => handleChange('description', e.target.value)} 
                  />
                </div>
              </div>
            </div>

          </div>

          {/* ── Footer Sticky ── */}
          <div style={modalFooterStyle}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>
              Annuler
            </button>
            <button 
              type="submit" 
              className="btn btn-primary btn-sm" 
              disabled={saving} 
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#dc2626', borderColor: '#dc2626', fontWeight: 600, padding: '8px 18px' }}
            >
              <FiCheck /> {saving ? 'Enregistrement...' : (isEditing ? 'Mettre à jour le litige' : 'Ouvrir le Dossier de Contentieux')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 2000, padding: '16px',
};
const modalContentStyle = {
  maxWidth: 880, width: '100%', maxHeight: 'calc(100vh - 32px)',
  borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column',
  background: '#ffffff', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
  border: '1px solid var(--border, #e2e8f0)',
};
const modalHeaderStyle = {
  padding: '14px 20px', borderBottom: '1px solid var(--border, #e2e8f0)',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  background: '#f8fafc', flexShrink: 0,
};
const modalFooterStyle = {
  padding: '12px 20px', borderTop: '1px solid var(--border, #e2e8f0)',
  display: 'flex', justifyContent: 'flex-end', gap: 10,
  background: '#f8fafc', flexShrink: 0,
};
const closeBtnStyle = {
  border: 'none', background: 'transparent', cursor: 'pointer',
  color: 'var(--text-secondary, #64748b)', padding: 4, borderRadius: 6,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const sectionBoxStyle = {
  padding: '12px 14px', borderRadius: 8,
  border: '1px solid var(--border, #e2e8f0)', background: '#ffffff',
};
const sectionTitleStyle = {
  fontSize: 12, fontWeight: 700, color: '#1e293b',
  marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em',
  display: 'flex', alignItems: 'center', gap: 6,
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4, display: 'block' };

export default LitigationFormModal;
